import React from "react";
import { connect } from "react-redux";
import parse from "style-to-js";
import _ from "lodash";
import interact from "interactjs";
import { $Button } from "us.common/components";
import { LockOutlined } from "us.icons";
import {
  drawGuides,
  hideGuides,
  getLockStatus,
  isEnableDrag,
  getMaxSizeForSection,
  getSectionRuleContent,
} from "../../Functions";
import { dragMoveListener, pasteAsPlainText } from "./Functions";
import * as Actions from "actions";
import "./USCSection.scss";
import {
  DEFAULT_DRAWER_STATE,
  DocumentType,
  DrawerType,
} from "constants/TemplateEditor";
import { getSectionByName, getSectionRuleByRule } from "functions";

function USCSection({
  id,
  children,
  style,
  defaultSection,
  hidden,
  rule,
  name,
  onClick,
  template,
  extraClassName,
  onChangeProperties,
  updateTemplate,
  locked,
  section,
  repeatingAdapterId,
  manageDrawer,
}) {
  const { templateContent } = template.data ?? {};

  interact.dynamicDrop(true);
  interact(`.usc-section-${id}`)
    .resizable({
      // resize from all edges and corners
      edges: {
        left: true,
        right: true,
        bottom: true,
        top: true,
      },

      listeners: {
        move(event) {
          var target = event.target;
          if (target) {
            showGuides(target);

            var x = parseFloat(target.getAttribute("data-x")) || 0;
            var y = parseFloat(target.getAttribute("data-y")) || 0;

            // update the element's style
            target.style.width = event.rect.width + "px";
            target.style.height = event.rect.height + "px";

            // translate when resizing from top or left edges
            x += event.deltaRect.left;
            y += event.deltaRect.top;

            target.style.transform = "translate(" + x + "px," + y + "px)";
            target.setAttribute("data-x", x);
            target.setAttribute("data-y", y);
          }
        },
        // call this function on every dragend event
        end(event) {
          // after end
          hideGuides();
          var target = event.target;
          if (target) {
            var x = parseFloat(target.getAttribute("data-x")) || 0;
            var y = parseFloat(target.getAttribute("data-y")) || 0;

            const left = parseInt(target?.style.left, 10);
            const top = parseInt(target?.style.top, 10);

            const rect = event.target.getBoundingClientRect();
            const properties = Object.assign(rect, {
              x: left + x,
              y: top + y,
            });
            onChangeProperties(properties, name, rule);
          }
        },
      },
      modifiers: [
        // keep the edges inside the parent
        interact.modifiers.restrictEdges({
          outer: "parent",
        }),
        // minimum size
        interact.modifiers.restrictSize({
          min: { width: 100, height: 50 },
          max: getMaxSizeForSection(section),
        }),
      ],
      inertia: true,
      enabled: !getLockStatus(id),
    })
    .draggable({
      listeners: {
        // call this function on every dragmove event
        move(event) {
          showGuides(event.target);
          dragMoveListener(event, id);
        },
        // call this function on every dragend event
        end(event) {
          // after end
          hideGuides();
          var target = event.target;
          if (target) {
            var x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
            var y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;

            const left = parseInt(target?.style.left, 10);
            const top = parseInt(target?.style.top, 10);

            const rect = event.target.getBoundingClientRect();
            const properties = Object.assign(rect, {
              x: left + x,
              y: top + y,
              dx: x,
              dy: y,
            });
            onChangeProperties(properties, name, rule);
          }
        },
      },
      inertia: true,
      enabled: isEnableDrag(id),
      // enable autoScroll
      autoScroll: true,
      modifiers: [
        interact.modifiers.restrictRect({
          restriction: "parent",
          endOnly: true,
        }),
      ],
    });

  const showGuides = (target) => {
    try {
      var id = target?.getAttribute("id");
      // show guides when resize
      if (id) {
        var pageId = `page_${id.substring(0, 1)}`;
        drawGuides(id, pageId);
      }
    } catch (error) {
      console.error("Show guides failed : ", error);
    }
  };

  const handleDoubleClick = (event) => {
    if (!getLockStatus(id)) {
      const parentElement = document.getElementById(id);
      const sectionName = parentElement.getAttribute("sectionname");
      const rule = parentElement.getAttribute("rule");
      const section = getSectionByName(sectionName, templateContent.pages);
      const sectionRule = getSectionRuleByRule(rule, section);
      const sectionRuleContent = getSectionRuleContent(sectionRule);

      if (sectionRule && section.name == sectionName) {
        const drawerType =
          templateContent.output.format == DocumentType.SMS
            ? DrawerType.SMS_EDITOR
            : DrawerType.RICH_TEXT_EDITOR;
        manageDrawer &&
          manageDrawer({
            ...DEFAULT_DRAWER_STATE,
            content: sectionRuleContent,
            visible: true,
            title: `${name}`,
            type: drawerType,
            size: "1120px",
          });
      } else {
        $Message.error(
          t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SECTION_SELECTION_FAILED")
        );
      }
    }
  };

  /**
   * @description - Handle lock functionality
   */
  const handleSectionLock = () => {
    try {
      const element = document.getElementById(id);
      if (getLockStatus(id)) {
        element?.removeAttribute("locked");
      } else {
        element?.setAttribute("locked", "true");
      }

      const pages = templateContent.pages.map((page) => {
        return {
          ...page,
          sections: page.sections.map((section) => {
            if (section.name == name) {
              return { ...section, locked: !section.locked };
            } else {
              return section;
            }
          }),
        };
      });

      const updatedTemplate = {
        ...template.data,
        templateContent: {
          ...templateContent,
          pages,
        },
      };
      updateTemplate && updateTemplate(updatedTemplate);
    } catch (error) {
      console.error("handleSectionLock failed : ", error);
    }
  };

  return (
    <div
      id={id}
      locked={locked}
      className={`usc-section box${extraClassName} usc-section-${id}`} //box absolutely-positioned
      height={"auto"}
      style={{
        ...parse(style),
      }}
      onClick={(event) => {
        event.stopPropagation();
        event.detail === 2 && handleDoubleClick(event);
        onClick(name);
      }}
      hidden={Boolean(hidden)}
      sectionname={name}
      rule={rule}
      defaultsection={defaultSection}
      onContextMenu={(event) => {
        event.preventDefault();
        onClick(name);
      }}
      repeatingadapterid={repeatingAdapterId}
    >
      <div className="w-100 h-100">
        {getLockStatus(id) && (
          <div className="lock-indicator">
            <$Button
              size="small"
              type="ghost"
              shape="circle"
              className="lock-indicator-button"
              icon={<LockOutlined />}
              onClick={() => handleSectionLock()}
            />
          </div>
        )}
        <div
          id={`usc-section-content-${id}`}
          className={`usc-section-content${
            getLockStatus(id) ? " prevent-select" : ""
          }`}
          defaultsection={defaultSection}
          onPaste={pasteAsPlainText}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  const { templatesHistory, templateEditor } = state;
  const { template } = templatesHistory;
  const { section, drawerInfo } = templateEditor;

  return {
    template,
    section,
    drawerInfo,
  };
};

const { template } = Actions.templatesHistory;
const { templateRules } = Actions.templateEditor;

const mapDispatchToProps = {
  updateTemplate: template.update,
  manageDrawer: templateRules.manageDrawer,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(USCSection);
